import api from '../api/api';

// ---------------------------------------------------------------------------
// Activity Trail logger
// Collects user activity events (login, page views, clicks, edits, deletes)
// in a queue and flushes them to the backend in small batches, so the UI
// never blocks and the API isn't spammed with one call per click.
// ---------------------------------------------------------------------------

const FLUSH_INTERVAL_MS = 7000;
const MAX_QUEUE_BEFORE_FLUSH = 15;

let queue = [];
let flushTimer = null;
let lastEventKey = '';
let lastEventTime = 0;

// Friendly page names for the trail (falls back to the raw path).
const PAGE_NAMES = {
  '/': 'Login',
  '/home': 'Dashboard',
  '/dashboard': 'Time Sheets',
  '/timesheet': 'Timesheet',
  '/recap': 'Recap',
  '/users': 'Users',
  '/report': 'Reports',
  '/devices': 'Devices',
  '/support': 'Support',
  '/safety': 'Safety',
  '/onboarding': 'Emp OnBoard',
  '/enroll': 'Re-Enroll',
  '/register': 'Admin',
  '/view-onboarding': 'Enrolled Employees',
  '/doc': 'Manual Doc',
  '/logs': 'Logs',
  '/otp-logs': 'Web OTP',
  '/activity-trail': 'Activity Trail',
  '/profile': 'Profile',
  '/docs': 'Docs',
  '/manual-dashboard': 'Tools and Resources',
  '/forms': 'Forms',
  '/merge-dashboard': 'Merge Dashboard',
  '/driver-form': 'Driver Form',
  '/accident-form': 'Accident Form',
  '/bbs-form': 'BBS Form',
  '/safety-form': 'Safety Form',
  '/trailer-form': 'Trailer Form',
  '/subcontractors': 'Subcontractors',
  '/drafts': 'Drafts',
  '/safety-analytics': 'Safety Analytics',
  '/job-analytics': 'Job Analytics',
};

export const pageNameFor = (pathname) => PAGE_NAMES[pathname] || pathname;

const hasToken = () => !!sessionStorage.getItem('accessToken');

export const flushActivity = async () => {
  if (!queue.length || !hasToken()) return;
  const events = queue.splice(0, queue.length);
  try {
    await api.post('/v1/activity/logs', { events });
  } catch (err) {
    // Never break the app because of activity logging.
    console.warn('Activity log flush failed:', err?.message || err);
  }
};

const ensureTimer = () => {
  if (flushTimer) return;
  flushTimer = setInterval(() => {
    if (queue.length) flushActivity();
  }, FLUSH_INTERVAL_MS);
};

export const logActivity = (action, { page, target, details } = {}) => {
  if (!hasToken()) return; // public pages / logged-out: skip

  // Drop identical events fired within 1s (double clicks, re-renders).
  const key = `${action}|${page || ''}|${target || ''}`;
  const now = Date.now();
  if (key === lastEventKey && now - lastEventTime < 1000) return;
  lastEventKey = key;
  lastEventTime = now;

  queue.push({
    action,
    page: page || pageNameFor(window.location.pathname),
    target: target || null,
    details: details || null,
  });

  ensureTimer();
  if (queue.length >= MAX_QUEUE_BEFORE_FLUSH || action === 'LOGIN' || action === 'LOGOUT') {
    flushActivity();
  }
};

// Classify a clicked control into a meaningful action from its label.
export const classifyClick = (label) => {
  if (/delete|remove|trash|discard/i.test(label)) return 'DELETE';
  if (/edit|update|save|modify|rename|approve|reject/i.test(label)) return 'EDIT';
  if (/add|create|new|submit|upload|enroll|register/i.test(label)) return 'CREATE';
  return 'CLICK';
};

// Best-effort flush when the tab is closed or hidden.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    if (!queue.length || !hasToken()) return;
    const events = queue.splice(0, queue.length);
    try {
      fetch(`${api.defaults.baseURL}/v1/activity/logs`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ events }),
      });
    } catch (e) {
      /* ignore */
    }
  });
}
